import React from "react";
import { Lead, Section, P, CTACard } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "Confiar en la IA a ciegas";
export const excerpt =
  "Nos acostumbramos a fusionar rápido trabajo escrito por completo por la IA. Pero el momento en que un agente se detiene no es el momento en que el trabajo se puede publicar sin riesgo. Una vez fusionado, sigue siendo tuyo.";
export const heroAlt = "Paisaje estilo Moebius: un árbol solitario en una ladera verde sobre un valle, con nubes rosadas y una luna creciente al anochecer";
export const readingTime = "4 min de lectura";

const BLUE = "#5ba8f5";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "Anthropic, «Common workflows», documentación de Claude Code. Se usa para los flujos de trabajo de Claude Code en torno a la exploración de código, las pruebas, la creación y revisión de pull requests, las tareas programadas, los worktrees, el modo de planificación y los subagentes.",
    href: "https://code.claude.com/docs/en/common-workflows",
    label: "code.claude.com/docs/en/common-workflows",
  },
  {
    text: "N. Perry, M. Srivastava, D. Kumar, D. Boneh, «Do Users Write More Insecure Code with AI Assistants?» (Stanford). Se usa para el hallazgo de que los participantes con un asistente de IA escribieron código bastante menos seguro y, aun así, eran más propensos a creer que su código era seguro.",
    href: "https://arxiv.org/abs/2211.03622",
    label: "arxiv.org/abs/2211.03622",
  },
  {
    text: "Sonar, «State of Code Developer Survey report» (2026). Se usa para el dato de que el 61% de los desarrolladores dice que la IA a menudo produce código que parece correcto pero no es fiable, que el 96% no confía del todo en la salida de la IA y que solo el 48% la verifica siempre: el cuello de botella de la verificación.",
    href: "https://www.sonarsource.com/blog/state-of-code-developer-survey-report-the-current-reality-of-ai-coding",
    label: "sonarsource.com/…/state-of-code-developer-survey-report",
  },
  {
    text: "Anthropic, «Code Review», documentación de Claude Code. Se usa para la revisión en paralelo de una pull request por varios agentes, un paso de verificación que filtra los falsos positivos y hallazgos deduplicados y ordenados por gravedad.",
    href: "https://code.claude.com/docs/en/code-review",
    label: "code.claude.com/docs/en/code-review",
  },
  {
    text: "OpenSSF, «Security-Focused Guide for AI Code Assistant Instructions». Se usa para el principio de que el desarrollador mantiene el control y la responsabilidad sobre el código generado por IA, y debería evaluarlo y editarlo con criterio en lugar de aceptarlo a ciegas.",
    href: "https://best.openssf.org/Security-Focused-Guide-for-AI-Code-Assistant-Instructions.html",
    label: "best.openssf.org/…/AI-Code-Assistant-Instructions",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        Nos adaptamos bastante rápido a escribir código con IA. Pero a mí me sorprende lo rápido que nos hemos
        acostumbrado a aceptar y fusionar, sin más, funcionalidades y reescrituras enteras escritas por completo por
        la IA. (Así lo siento yo.)
      </Lead>

      <P>
        El flujo de trabajo de Claude ya no va realmente de generar una función, va de usar Claude para explorar
        un código que no conoces, seguir un flujo de autenticación, añadir pruebas, abrir una pull request, retomar
        sesiones antiguas, correr trabajo en paralelo en worktrees, planificar antes de editar, delegar la
        investigación en subagentes e incluso ejecutar tareas programadas como revisar las PR abiertas.<Fn n={1} /> Eso
        se parece más a un desarrollador júnior que se mueve por el repositorio y deja tras de sí un rastro que tiene
        pinta de trabajo.
      </P>
      <P>
        Y ahí es donde se pone incómodo. Cuando un agente termina, la salida suele tener la forma de un trabajo
        terminado: un plan, archivos modificados, comandos que se ejecutaron, un resumen, a veces una descripción de
        la PR, a veces la salida de las pruebas, a veces una nota sobre qué riesgos vigilar. Le da al trabajo el
        aspecto y la sensación de estar terminado. A menudo me pillo confiando en esta salida a ciegas. Por pura
        pereza. Aunque sé que la salida de un LLM sigue siendo solo probabilidad matemática, no la verdad.<Fn n={1} /> La
        salida puede ser útil y aun así no ser definitiva.
      </P>
      <P>
        El trabajo no es falso. No es un sinsentido. La mayoría de las veces es plausible y coherente, compila y
        suele pasar las pruebas. El agente hizo algo, quizá incluso lo correcto. Pero aun así podría provocar bugs,
        errores y cosas peores.<Fn n={2} />
      </P>

      <Section title="¿Y las revisiones?">
        <P>
          Con el autocompletado, la primera generación de herramientas como GitHub Copilot, Cursor y Windsurf, la
          revisión seguía formando parte del proceso y era pequeña por defecto. Una sugerencia, unas pocas líneas,
          aceptar o rechazar. Incluso cuando se equivocaba, tú estabas justo al lado del código, así que el error se
          quedaba en lo local. Hoy esas mismas herramientas entregan funcionalidades enteras y grandes bloques de
          golpe, a menudo varios miles de líneas de código en una sola ejecución, y la revisión nunca se encogió para
          estar a la altura.
        </P>
        <P>
          Trabajar con flujos agénticos o flujos multiagente como los flujos dinámicos de Claude Code no es lo mismo.
          El agente busca, edita, ejecuta una prueba, lee el error, vuelve a editar, quizá instala un paquete, quizá
          abre una rama y luego resume todo el trabajo en un tono que suena definitivo (y con los enjambres de agentes
          va a peor). Así que la pregunta cambia. Ya no solo te preguntas si el código es correcto. Te preguntas si
          este era el camino adecuado a través del problema. ¿Leyó los archivos correctos? ¿Interpretó mal la
          arquitectura? ¿Tomó un atajo porque las pruebas eran débiles? ¿Cambió el comportamiento público mientras
          solo comprobaba el caso feliz?
        </P>
        <P>
          Eso es mucho más difícil de revisar porque la mitad de lo que necesitas revisar es invisible. El diff está
          ahí; el razonamiento que hay detrás, casi nunca. El modo de planificación y los subagentes ayudan: el agente
          propone un plan antes de tocar archivos y puede investigar en un contexto aparte e informar de vuelta.
          <Fn n={1} /> Cuanto más capaz es el agente, más necesita la persona un registro del camino, no solo del parche.
        </P>
      </Section>

      <Section title="«Terminado» ya no significa terminado">
        <P>
          Seguimos diciendo «terminado» como si significara una sola cosa. Pero en la programación agéntica «terminado»
          no es un único estado, es toda una pila de afirmaciones separadas. El agente se detuvo. El código compila.
          Las pruebas pasan. El riesgo es aceptable. El desarrollador entiende de verdad el cambio. Un agente puede
          cumplir algunas de esas y saltarse el resto en silencio. Puede resolver la tarea y aun así dejar un agujero.
          Puede ejecutar pruebas y aun así saltarse justo el comportamiento que importaba. Para mí esa es la parte que
          se difumina: «el agente se detuvo» y «esto se puede publicar sin riesgo» parecen el mismo momento, pero no lo
          son, y ahí es donde se traza la línea entre programar a ojo y programar.
        </P>
        <P>
          Así que «los agentes van a reemplazar a los desarrolladores» no es, para mí, el miedo equivocado. Costará
          puestos de trabajo, ya lo hace, sobre todo para los desarrolladores júnior. Pero el enfoque sigue estando
          descolocado. El problema de verdad es más soso y corre por debajo de todo esto: los agentes aumentan cuánto
          código producimos más rápido de lo que aumentan cuánto código podemos verificar de verdad.<Fn n={3} /> Esa
          brecha tiene nombre. Se llama deuda de revisión, y va en aumento.
        </P>
      </Section>

      <Section title="Sigue siendo tuyo">
        <P>
          El agente puede escribir el parche, explicarlo, ejecutar la prueba, incluso revisarlo. El resultado sigue
          siendo tuyo en el momento en que se fusiona.<Fn n={5} /> No le puedes decir a un cliente que el bug es menos
          real porque lo escribió un agente. No le puedes decir al siguiente ingeniero que nadie entiende el código
          porque lo escribió Claude a la 1:37 de la madrugada y la PR tenía buena pinta.
        </P>
        <P>
          La propiedad nunca fue, en realidad, teclear cada carácter. Ya nos apoyamos en librerías, frameworks,
          clientes generados, andamiajes, CI. El trabajo siempre fue el criterio. Los agentes solo lo hacen más difícil
          de ignorar.
        </P>
        <P>
          Cada vez más, el revisor es otro agente. El agente escribe el código, el agente revisa el código.<Fn n={4} /> Eso
          puede funcionar, pero también puede limitarse a apilar dos resúmenes confiados uno encima del otro y llamarlo
          consenso. Cuando mi cofundador (mi hermano) y yo empezamos a trabajar en Cate, y en otros proyectos antes de
          ese, nos topamos con todo esto nosotros mismos. Fusionábamos cosas demasiado rápido porque el diff tenía buena
          pinta y queríamos avanzar deprisa. Sobre todo con los últimos lanzamientos de Cate, en los que la app se llenó
          de más funcionalidades y quedó más pulida, salieron a la luz un montón de bugs, sistemas de arquitectura
          duplicados y código repetitivo. Lo limpiamos con un trabajo largo, duro y todavía en curso. Pero lo que
          aprendimos y nos llevamos de ello es que queremos hacer de la revisión de código dentro de Cate uno de los
          focos principales del futuro cercano. Y creemos que, con el concepto de canvas abierto, tenemos la mejor base,
          y suficiente «espacio», para hacer esto lo más visual y eficiente posible.
        </P>
        <P>
          Haz que el trabajo del agente sea inspeccionable: muestra la tarea, muestra el plan, muestra los archivos que
          leyó y modificó, muestra los comandos, muestra la salida de las pruebas, muestra las aprobaciones y muestra
          los huecos. Dalo como un resumen visual claro sobre el canvas, para que la cadena de trabajo siga a la vista
          mientras la revisas, en lugar de quedar enterrada en un log de chat o en un párrafo confiado.
        </P>
        <P>Y luego deja que el desarrollador la revise como si fuera suya.</P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          Porque lo es.
        </p>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="Referencias">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
